import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { supabase } from "../supabaseClient";
import { selectUser } from "../slices/userReducer";

const DISMISS_PREFIX = "mp_dismiss_";
const TTL_MS = { "24h": 86400000, "7d": 604800000 };

const pathMatches = (pattern, path) =>
  pattern === "/" ? path === "/" : path.startsWith(pattern);

export default function useMarketingPopup() {
  const location = useLocation();
  const user = useSelector(selectUser);
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const viewedRef = useRef(false);

  const isLoggedIn = !!user?.id;

  // ── Fetch config ──
  useEffect(() => {
    let cancelled = false;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const { data } = await supabase.rpc("get_active_marketing_popup");
        if (!cancelled) setConfig(data || null);
      } catch {
        if (!cancelled) setConfig(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConfig();
    return () => { cancelled = true; };
  }, []);

  // ── Trigger & targeting logic ──
  useEffect(() => {
    if (!config) return;
    setVisible(false);
    clearTimeout(timerRef.current);

    // ── Visitor targeting ──
    const targeting = config.visitor_targeting || "all";
    if (targeting === "non_members" && isLoggedIn) return;
    if (targeting === "new_visitors") {
      const visited = localStorage.getItem("22night_visited_before");
      if (visited) return;
      try { localStorage.setItem("22night_visited_before", "1"); } catch {}
    }

    // ── Page exclusions / targeting ──
    const currentPath = location.pathname;
    const excludes = config.exclude_pages || [];
    if (excludes.some((p) => pathMatches(p, currentPath))) return;

    const targets = config.target_pages || [];
    if (targets.length > 0 && !targets.some((p) => pathMatches(p, currentPath))) return;

    // ── Dismissal check ──
    const dismissedKey = DISMISS_PREFIX + config.id;
    const ttl = config.dismiss_ttl || "session";

    if (ttl === "never") {
      // Always show — no dismissal check
    } else if (ttl === "session") {
      if (sessionStorage.getItem(dismissedKey)) return;
    } else {
      const raw = localStorage.getItem(dismissedKey);
      if (raw) {
        try {
          const { ts } = JSON.parse(raw);
          if (TTL_MS[ttl] && Date.now() - ts < TTL_MS[ttl]) return;
        } catch {}
      }
    }

    // ── Trigger ──
    const trigger = config.trigger_type || "timer";

    if (trigger === "immediate") {
      timerRef.current = setTimeout(showPopup, 100);
    } else if (trigger === "exit_intent") {
      const handler = (e) => {
        if (e.clientY <= 0 && e.relatedTarget === null) {
          document.removeEventListener("mouseout", handler);
          showPopup();
        }
      };
      document.addEventListener("mouseout", handler);
      return () => document.removeEventListener("mouseout", handler);
    } else if (trigger === "scroll") {
      const threshold = (config.scroll_threshold || 50) / 100;
      const handler = () => {
        const h = document.documentElement;
        const pct = h.scrollTop / (h.scrollHeight - h.clientHeight);
        if (pct >= threshold) {
          window.removeEventListener("scroll", handler);
          showPopup();
        }
      };
      window.addEventListener("scroll", handler, { passive: true });
      return () => window.removeEventListener("scroll", handler);
    } else {
      // timer (default)
      const delay = (config.delay_seconds || 10) * 1000;
      timerRef.current = setTimeout(showPopup, delay);
    }

    return () => clearTimeout(timerRef.current);
  }, [config, location.pathname, isLoggedIn]);

  // Show popup & record view
  function showPopup() {
    setVisible(true);
    if (config && !viewedRef.current) {
      viewedRef.current = true;
      supabase.rpc("record_popup_view", { popup_id: config.id }).then(null, () => {});
    }
  }

  // ── Dismiss ──
  const dismiss = useCallback(() => {
    setVisible(false);
    if (!config) return;
    const key = DISMISS_PREFIX + config.id;
    const ttl = config.dismiss_ttl || "session";

    if (ttl === "never") return;
    if (ttl === "session") {
      sessionStorage.setItem(key, "1");
    } else {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now() }));
    }
  }, [config]);

  return { config, visible, loading, dismiss, isLoggedIn };
}
